import { useMemo, useState } from 'react';
import { useQuery } from '@tanstack/react-query';
import { useTranslation } from 'react-i18next';
import {
    Accordion,
    AccordionDetails,
    AccordionSummary,
    Alert,
    Box,
    Button,
    Checkbox,
    CircularProgress,
    FormControlLabel,
    IconButton,
    MenuItem,
    Pagination,
    Stack,
    Table,
    TableBody,
    TableCell,
    TableHead,
    TableRow,
    TextField,
    Tooltip,
    Typography,
} from '@mui/material';
import ExpandMoreIcon from '@mui/icons-material/ExpandMore';
import ManageSearchIcon from '@mui/icons-material/ManageSearch';
import PlayArrowIcon from '@mui/icons-material/PlayArrow';
import GraphicEqIcon from '@mui/icons-material/GraphicEq';
import DownloadIcon from '@mui/icons-material/Download';
import {
    MusicAlbumRow,
    MusicSearchField,
    MusicSearchParams,
    MusicSearchSort,
    MusicTrack,
    formatDuration,
    musicService,
} from '../../api/music';
import { buildAlbumTree } from './AlbumTree';
import { StarRating } from './StarRating';
import { useMusicPlayer } from './MusicPlayerContext';

const PAGE_SIZE = 50;
const FIELDS: MusicSearchField[] = ['title', 'artist', 'album', 'filename'];
const SORTS: MusicSearchSort[] = ['title', 'artist', 'album', 'rating', 'duration', 'size'];

type RatingMode = 'any' | 'min' | 'exact';

interface AdvancedSearchProps {
    albums: MusicAlbumRow[];
    tracks: MusicTrack[];
    onRate: (track: MusicTrack, rating: number) => void;
    onDownload: (track: MusicTrack) => void;
}

const extensionOf = (track: MusicTrack): string => {
    const name = track.original_name ?? '';
    const dot = name.lastIndexOf('.');
    return dot >= 0 ? name.slice(dot + 1).toLowerCase() : '';
};

const minutesToSeconds = (value: string): number | undefined => {
    const parsed = Number(value.replace(',', '.'));
    return value.trim() !== '' && Number.isFinite(parsed) && parsed >= 0 ? Math.round(parsed * 60) : undefined;
};

/** Separate "advanced search" section: filters + exact (whole word / phrase) text search, evaluated on the server. */
export const AdvancedSearch = ({ albums, tracks, onRate, onDownload }: AdvancedSearchProps) => {
    const { t } = useTranslation();
    const player = useMusicPlayer();
    const [q, setQ] = useState('');
    const [exact, setExact] = useState(true);
    const [fields, setFields] = useState<MusicSearchField[]>(FIELDS);
    const [albumPath, setAlbumPath] = useState('');
    const [source, setSource] = useState('');
    const [sourceNot, setSourceNot] = useState(false);
    const [ratingMode, setRatingMode] = useState<RatingMode>('any');
    const [ratingValue, setRatingValue] = useState(4);
    const [extensions, setExtensions] = useState<string[]>([]);
    const [minMinutes, setMinMinutes] = useState('');
    const [maxMinutes, setMaxMinutes] = useState('');
    const [sort, setSort] = useState<MusicSearchSort>('title');
    const [direction, setDirection] = useState<'asc' | 'desc'>('asc');
    const [applied, setApplied] = useState<MusicSearchParams | null>(null);
    const [page, setPage] = useState(1);

    const albumOptions = useMemo(() => [...buildAlbumTree(albums).nodes.values()].sort((a, b) => a.path.localeCompare(b.path)), [albums]);
    const sourceOptions = useMemo(() => [...new Set(tracks.map((track) => track.source).filter((value): value is string => Boolean(value)))].sort(), [tracks]);
    const extensionOptions = useMemo(() => [...new Set(tracks.map(extensionOf).filter(Boolean))].sort(), [tracks]);

    const resultsQuery = useQuery({
        queryKey: ['music-tracks', 'advanced', applied, page],
        queryFn: () => musicService.search({ ...(applied as MusicSearchParams), page, perPage: PAGE_SIZE }),
        enabled: applied !== null,
        placeholderData: (previous) => previous,
    });
    const results = resultsQuery.data?.tracks ?? [];

    const toggle = <T,>(list: T[], value: T): T[] => (list.includes(value) ? list.filter((item) => item !== value) : [...list, value]);

    const run = () => {
        setPage(1);
        setApplied({
            q: q.trim() || undefined,
            exact: exact && q.trim() !== '',
            fields: fields.length > 0 && fields.length < FIELDS.length ? fields : undefined,
            albumPath: albumPath || undefined,
            source: source && !sourceNot ? source : undefined,
            sourceNot: source && sourceNot ? source : undefined,
            rating: ratingMode === 'exact' ? ratingValue : undefined,
            minRating: ratingMode === 'min' ? ratingValue : undefined,
            extensions: extensions.length > 0 ? extensions : undefined,
            minDuration: minutesToSeconds(minMinutes),
            maxDuration: minutesToSeconds(maxMinutes),
            sort,
            direction,
        });
    };

    const reset = () => {
        setQ('');
        setExact(true);
        setFields(FIELDS);
        setAlbumPath('');
        setSource('');
        setSourceNot(false);
        setRatingMode('any');
        setRatingValue(4);
        setExtensions([]);
        setMinMinutes('');
        setMaxMinutes('');
        setSort('title');
        setDirection('asc');
        setApplied(null);
        setPage(1);
    };

    return (
        <Accordion variant="outlined" disableGutters sx={{ mb: 2 }}>
            <AccordionSummary expandIcon={<ExpandMoreIcon />}>
                <Stack direction="row" spacing={1} alignItems="center">
                    <ManageSearchIcon />
                    <Typography variant="subtitle1">{t('music.advanced.title')}</Typography>
                </Stack>
            </AccordionSummary>
            <AccordionDetails>
                <Stack spacing={2}>
                    <Stack direction={{ xs: 'column', md: 'row' }} spacing={2} alignItems={{ md: 'center' }}>
                        <TextField
                            size="small"
                            fullWidth
                            label={t('music.advanced.text')}
                            value={q}
                            onChange={(event) => setQ(event.target.value)}
                            onKeyDown={(event) => {
                                if (event.key === 'Enter') run();
                            }}
                        />
                        <FormControlLabel
                            sx={{ whiteSpace: 'nowrap', mr: 0 }}
                            control={<Checkbox checked={exact} onChange={(event) => setExact(event.target.checked)} />}
                            label={t('music.advanced.exact')}
                        />
                    </Stack>
                    <Typography variant="caption" color="text.secondary" sx={{ mt: '-8px !important' }}>{t('music.advanced.exactHint')}</Typography>

                    <Stack direction="row" flexWrap="wrap" alignItems="center" useFlexGap spacing={0.5}>
                        <Typography variant="body2" color="text.secondary" sx={{ mr: 1 }}>{t('music.advanced.searchIn')}</Typography>
                        {FIELDS.map((field) => (
                            <FormControlLabel
                                key={field}
                                control={<Checkbox size="small" checked={fields.includes(field)} onChange={() => setFields((prev) => toggle(prev, field))} />}
                                label={t(`music.advanced.field.${field}`)}
                            />
                        ))}
                    </Stack>

                    <Stack direction={{ xs: 'column', md: 'row' }} spacing={2}>
                        <TextField select size="small" fullWidth label={t('music.album')} value={albumPath} onChange={(event) => setAlbumPath(event.target.value)}>
                            <MenuItem value="">{t('music.all')}</MenuItem>
                            {albumOptions.map((node) => (
                                <MenuItem key={node.path} value={node.path}>{node.path.split('/').join(' / ')} ({node.totalCount})</MenuItem>
                            ))}
                        </TextField>
                        <Stack direction="row" spacing={1} alignItems="center" sx={{ width: '100%' }}>
                            <TextField select size="small" fullWidth label={t('music.source')} value={source} onChange={(event) => setSource(event.target.value)}>
                                <MenuItem value="">{t('music.all')}</MenuItem>
                                {sourceOptions.map((option) => <MenuItem key={option} value={option}>{option}</MenuItem>)}
                            </TextField>
                            <FormControlLabel
                                sx={{ whiteSpace: 'nowrap', mr: 0 }}
                                disabled={!source}
                                control={<Checkbox size="small" checked={sourceNot} onChange={(event) => setSourceNot(event.target.checked)} />}
                                label={t('music.advanced.sourceNot')}
                            />
                        </Stack>
                    </Stack>

                    <Stack direction={{ xs: 'column', md: 'row' }} spacing={2} alignItems={{ md: 'center' }}>
                        <TextField select size="small" sx={{ minWidth: 200 }} label={t('music.rating')} value={ratingMode} onChange={(event) => setRatingMode(event.target.value as RatingMode)}>
                            <MenuItem value="any">{t('music.advanced.ratingAny')}</MenuItem>
                            <MenuItem value="min">{t('music.advanced.ratingMin')}</MenuItem>
                            <MenuItem value="exact">{t('music.advanced.ratingExact')}</MenuItem>
                        </TextField>
                        {ratingMode !== 'any' && (
                            <Stack direction="row" spacing={1} alignItems="center">
                                <StarRating value={ratingValue} size="medium" onChange={(value) => setRatingValue(value)} />
                                <Typography variant="body2" color="text.secondary">
                                    {ratingMode === 'exact' && ratingValue === 0 ? t('music.advanced.unrated') : `${ratingValue}`}
                                </Typography>
                            </Stack>
                        )}
                        <TextField size="small" type="number" sx={{ width: 150 }} label={t('music.advanced.minMinutes')} value={minMinutes} onChange={(event) => setMinMinutes(event.target.value)} slotProps={{ htmlInput: { min: 0, step: 0.5 } }} />
                        <TextField size="small" type="number" sx={{ width: 150 }} label={t('music.advanced.maxMinutes')} value={maxMinutes} onChange={(event) => setMaxMinutes(event.target.value)} slotProps={{ htmlInput: { min: 0, step: 0.5 } }} />
                    </Stack>

                    {extensionOptions.length > 0 && (
                        <Stack direction="row" flexWrap="wrap" alignItems="center" useFlexGap spacing={0.5}>
                            <Typography variant="body2" color="text.secondary" sx={{ mr: 1 }}>{t('music.advanced.format')}</Typography>
                            {extensionOptions.map((extension) => (
                                <FormControlLabel
                                    key={extension}
                                    control={<Checkbox size="small" checked={extensions.includes(extension)} onChange={() => setExtensions((prev) => toggle(prev, extension))} />}
                                    label={`.${extension}`}
                                />
                            ))}
                        </Stack>
                    )}

                    <Stack direction={{ xs: 'column', md: 'row' }} spacing={2} alignItems={{ md: 'center' }}>
                        <TextField select size="small" sx={{ minWidth: 180 }} label={t('music.advanced.sortBy')} value={sort} onChange={(event) => setSort(event.target.value as MusicSearchSort)}>
                            {SORTS.map((value) => <MenuItem key={value} value={value}>{t(`music.advanced.sort.${value}`)}</MenuItem>)}
                        </TextField>
                        <TextField select size="small" sx={{ minWidth: 150 }} value={direction} onChange={(event) => setDirection(event.target.value as 'asc' | 'desc')}>
                            <MenuItem value="asc">{t('music.advanced.asc')}</MenuItem>
                            <MenuItem value="desc">{t('music.advanced.desc')}</MenuItem>
                        </TextField>
                        <Box sx={{ flex: 1 }} />
                        <Button onClick={reset}>{t('music.advanced.reset')}</Button>
                        <Button variant="contained" startIcon={<ManageSearchIcon />} onClick={run} disabled={fields.length === 0}>{t('music.advanced.search')}</Button>
                    </Stack>

                    {applied !== null && (
                        <Box>
                            {resultsQuery.isFetching && <CircularProgress size={20} />}
                            {resultsQuery.isError && <Alert severity="error">{t('common.errorFetchingData')}</Alert>}
                            {resultsQuery.data && (
                                <Typography variant="body2" color="text.secondary" sx={{ mb: 1 }}>
                                    {t('music.advanced.results', { count: resultsQuery.data.total })}
                                </Typography>
                            )}
                            {resultsQuery.data && results.length > 0 && (
                                <Table size="small">
                                    <TableHead>
                                        <TableRow>
                                            <TableCell width={48} />
                                            <TableCell>{t('music.trackTitle')}</TableCell>
                                            <TableCell>{t('music.artist')}</TableCell>
                                            <TableCell sx={{ display: { xs: 'none', md: 'table-cell' } }}>{t('music.album')}</TableCell>
                                            <TableCell>{t('music.rating')}</TableCell>
                                            <TableCell align="right">{t('music.duration')}</TableCell>
                                            <TableCell width={48} />
                                        </TableRow>
                                    </TableHead>
                                    <TableBody>
                                        {results.map((track, index) => {
                                            const isCurrent = player.current?.id === track.id;
                                            return (
                                                <TableRow key={track.id} hover selected={isCurrent} onDoubleClick={() => player.playQueue(results, index, null)}>
                                                    <TableCell>
                                                        <IconButton size="small" onClick={() => (isCurrent ? player.toggle() : player.playQueue(results, index, null))}>
                                                            {isCurrent && player.isPlaying ? <GraphicEqIcon color="primary" /> : <PlayArrowIcon />}
                                                        </IconButton>
                                                    </TableCell>
                                                    <TableCell sx={{ fontWeight: isCurrent ? 600 : undefined }}>{track.title}</TableCell>
                                                    <TableCell>{track.artist || '—'}</TableCell>
                                                    <TableCell sx={{ display: { xs: 'none', md: 'table-cell' } }}>{track.album_path || track.album || '—'}</TableCell>
                                                    <TableCell sx={{ whiteSpace: 'nowrap' }}>
                                                        <StarRating value={track.rating} onChange={(rating) => onRate(track, rating)} />
                                                    </TableCell>
                                                    <TableCell align="right">{formatDuration(track.duration_seconds)}</TableCell>
                                                    <TableCell>
                                                        <Tooltip title={t('music.download')}>
                                                            <IconButton size="small" onClick={() => onDownload(track)}>
                                                                <DownloadIcon fontSize="small" />
                                                            </IconButton>
                                                        </Tooltip>
                                                    </TableCell>
                                                </TableRow>
                                            );
                                        })}
                                    </TableBody>
                                </Table>
                            )}
                            {resultsQuery.data && resultsQuery.data.lastPage > 1 && (
                                <Pagination sx={{ mt: 2 }} count={resultsQuery.data.lastPage} page={page} onChange={(_event, value) => setPage(value)} />
                            )}
                        </Box>
                    )}
                </Stack>
            </AccordionDetails>
        </Accordion>
    );
};
